import {useMemo} from 'react';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {BulletinViewType} from '../BulletinForm';

export type BulletinPageMode = 'create' | 'edit' | 'view';

export interface UseBulletinsBreadcrumbsOptions {
  mode: BulletinPageMode;
  id?: number;
  title?: string;
  view?: BulletinViewType;
}

interface UseBulletinsBreadcrumbsResult {
  backBarProps: {fallbackTo: string; breadcrumbs: BreadcrumbItem[]};
}

/**
 * Hook to generate consistent BackBar props for Bulletin pages.
 */
export const useBulletinsBreadcrumbs = ({
  mode,
  id,
  title,
  view,
}: UseBulletinsBreadcrumbsOptions): UseBulletinsBreadcrumbsResult => {
  const backBarProps = useMemo(() => {
    // All bulletin detail pages (view/edit/create) are accessed from the Manage Bulletins tab
    const manageBulletinsPath = PathUtility.BulletinDashboard('manage');

    const breadcrumbs: BreadcrumbItem[] = [
      {label: 'Manage Bulletins', to: manageBulletinsPath},
    ];

    switch (mode) {
      case 'create':
        breadcrumbs.push({label: 'New Bulletin'});
        break;

      case 'edit':
        if (id) {
          breadcrumbs.push({
            label: title || `Bulletin #${id}`,
            to: PathUtility.BulletinView(id, view ?? 'content'),
          });
        }
        breadcrumbs.push({label: 'Edit'});
        break;

      case 'view':
        breadcrumbs.push({label: title || 'View Bulletin'});
        break;
    }

    const fallbackTo =
      mode === 'edit' && id
        ? PathUtility.BulletinView(id, view ?? 'content')
        : manageBulletinsPath;

    return {fallbackTo, breadcrumbs};
  }, [mode, id, title, view]);

  return {backBarProps};
};
